import React, { ReactNode, useCallback, useMemo } from 'react';
import {
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { TrendingDownRounded, TrendingUpRounded } from '@mui/icons-material';
import { DealTypes } from 'api';
import { useStoreContext } from 'store';
import { colors } from 'theme/constants';
import { formatCurrency } from 'helpers/formatters';
import { getAttributionModelField } from 'helpers/common';
import { TooltipChip, ColorCard } from 'components';

interface Props {
  deal: DealTypes.ItemDataExtended;
}

type InfoItems = {
  key: keyof DealTypes.ItemDataExtended;
  label: string;
  labelCaption?: string;
  potential: ReactNode;
  actual: ReactNode;
  trend: 'up' | 'down';
}[];

const BudgetComparisonCard = (props: Props): JSX.Element | null => {
  const { deal } = props;
  const {
    state: { attributionModel },
  } = useStoreContext();

  const getTrend = useCallback(
    (
      actualKey: keyof DealTypes.ItemDataExtended,
      potentialKey: keyof DealTypes.ItemDataExtended,
    ) => ((deal[potentialKey] || 0) < (deal[actualKey] || 0) ? 'up' : 'down'),
    [deal],
  );

  const tableFields: InfoItems = useMemo(
    () => [
      {
        key: 'budget_actual',
        label: 'Budget',
        potential: formatCurrency(deal.budget_potential || 0),
        actual: formatCurrency(deal.budget_actual || 0),
        trend: getTrend('budget_actual', 'budget_potential'),
      },
      {
        key: getAttributionModelField(attributionModel, 'revenue_actual'),
        label: 'Revenue',
        labelCaption: `(${attributionModel})`,
        potential: formatCurrency(deal.revenue_potential || 0),
        actual: formatCurrency(
          deal[getAttributionModelField(attributionModel, 'revenue_actual')] ||
            0,
        ),
        trend: getTrend(
          getAttributionModelField(attributionModel, 'revenue_actual'),
          'revenue_potential',
        ),
      },
      {
        key: getAttributionModelField(attributionModel, 'roi_actual'),
        label: 'ROI',
        labelCaption: `(${attributionModel})`,
        potential: deal.roi_potential || 0,
        actual:
          deal[getAttributionModelField(attributionModel, 'roi_actual')] || 0,
        trend: getTrend(
          getAttributionModelField(attributionModel, 'roi_actual'),
          'roi_potential',
        ),
      },
      {
        key: getAttributionModelField(attributionModel, 'gross_profit_actual'),
        label: 'Gross profit',
        labelCaption: `(${attributionModel})`,
        potential: formatCurrency(deal.gross_profit_potential || 0),
        actual: formatCurrency(
          deal[
            getAttributionModelField(attributionModel, 'gross_profit_actual')
          ] || 0,
        ),
        trend: getTrend(
          getAttributionModelField(attributionModel, 'gross_profit_actual'),
          'gross_profit_potential',
        ),
      },
    ],
    [deal, getTrend, attributionModel],
  );

  return (
    <ColorCard color={colors.yellow}>
      <CardHeader
        title="Budget comparison"
        subheader={
          <Typography variant="inherit">
            Comparison of potential metrics with actual
          </Typography>
        }
      />
      <CardContent>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align="right">Potential</TableCell>
              <TableCell align="right">Actual</TableCell>
              <TableCell align="right">Trend</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableFields.map((row) => (
              <TableRow
                key={row.key}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {row.label}
                  {row.labelCaption && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      ml={1}
                    >
                      {row.labelCaption}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography color="text.secondary" variant="inherit">
                    {row.potential}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography color="text.secondary" variant="inherit">
                    {row.actual}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <TooltipChip
                    title="Trend"
                    color={row.trend === 'up' ? 'success' : 'warning'}
                    sx={{
                      '& span': {
                        display: 'flex',
                        padding: 1,
                      },
                    }}
                    label={
                      row.trend === 'up' ? (
                        <TrendingUpRounded />
                      ) : (
                        <TrendingDownRounded />
                      )
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </ColorCard>
  );
};

export default BudgetComparisonCard;
